import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    collection,
    getDocs,
    doc,
    deleteDoc,
    query,
    orderBy,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import {
    Plus,
    Search,
    Filter,
    Calendar,
    DollarSign,
    Edit3,
    Trash2,
    AlertCircle,
    CheckCircle,
    X,
    ArrowUpDown,
    Building2,
    Loader2,
    ChevronLeft,
    MapPin,
    Settings,
    BarChart3,
    TrendingUp,
    Receipt,
    Tag,
    Download,
    FileText,
    PieChart,
    Briefcase,
    Users,
    Clock,
    Award,
    Gift,
    PiggyBank,
    Home,
    Zap,
    Target,
} from "lucide-react";

const XFinanceIncomesList = () => {
    const { user } = useUserAuth();
    const navigate = useNavigate();

    const [incomes, setIncomes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterCategory, setFilterCategory] = useState("all");
    const [filterType, setFilterType] = useState("all");
    const [filterDate, setFilterDate] = useState("mes");
    const [sortBy, setSortBy] = useState("fecha");
    const [loading, setLoading] = useState(false);
    const [loadingIncomes, setLoadingIncomes] = useState(true);
    const [message, setMessage] = useState({ text: "", type: "" });

    const periodOptions = [
        { value: "dia", label: "Hoy" },
        { value: "semana", label: "Esta Semana" },
        { value: "mes", label: "Este Mes" },
        { value: "trimestre", label: "Trimestre" },
        { value: "año", label: "Este Año" },
        { value: "all", label: "Todos" },
    ];

    const incomeTypeLabels = {
        "unico": "Pago Único",
        "recurrente": "Recurrente",
        "por_horas": "Por Horas"
    };

    const iconOptions = {
        "Briefcase": Briefcase,
        "DollarSign": DollarSign,
        "TrendingUp": TrendingUp,
        "Award": Award,
        "Gift": Gift,
        "PiggyBank": PiggyBank,
        "Home": Home,
        "Zap": Zap,
        "Target": Target,
        "Tag": Tag,
    };

    // Verificar autenticación
    useEffect(() => {
        if (!user) {
            navigate("/login");
        }
    }, [user, navigate]);

    // Cargar datos
    useEffect(() => {
        if (user) {
            loadIncomes();
            loadCategories();
        }
    }, [user]);

    const loadIncomes = async () => {
        if (!user) return;

        setLoadingIncomes(true);
        try {
            const incomesRef = collection(db, "incomes", user.uid, "userIncomes");
            const q = query(incomesRef, orderBy("createdAt", "desc"));
            const querySnapshot = await getDocs(q);

            const incomesData = [];
            querySnapshot.forEach((doc) => {
                incomesData.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });

            setIncomes(incomesData);
        } catch (error) {
            console.error("Error al cargar ingresos:", error);
            setMessage({ text: "Error al cargar los ingresos", type: "error" });
        } finally {
            setLoadingIncomes(false);
        }
    };

    const loadCategories = async () => {
        if (!user) return;

        try {
            const categoriesRef = collection(db, "incomes", user.uid, "incomeCategories");
            const q = query(categoriesRef, orderBy("nombre", "asc"));
            const querySnapshot = await getDocs(q);

            const categoriesData = [];
            querySnapshot.forEach((doc) => {
                categoriesData.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });

            setCategories(categoriesData);
        } catch (error) {
            console.error("Error al cargar categorías:", error);
        }
    };

    const handleDelete = async (incomeId, incomeDescription) => {
        if (!user || !window.confirm(`¿Está seguro de eliminar el ingreso "${incomeDescription}"?`)) {
            return;
        }

        setLoading(true);
        try {
            const incomeRef = doc(db, "incomes", user.uid, "userIncomes", incomeId);
            await deleteDoc(incomeRef);
            setMessage({ text: "Ingreso eliminado correctamente", type: "success" });
            loadIncomes();
        } catch (error) {
            console.error("Error al eliminar ingreso:", error);
            setMessage({ text: "Error al eliminar el ingreso", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const getDateRange = (period) => {
        const today = new Date();
        const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        switch (period) {
            case "dia":
                return {
                    start: startOfDay,
                    end: new Date(startOfDay.getTime() + 24 * 60 * 60 * 1000 - 1)
                };
            case "semana":
                const startOfWeek = new Date(startOfDay);
                startOfWeek.setDate(today.getDate() - today.getDay());
                return {
                    start: startOfWeek,
                    end: new Date(startOfWeek.getTime() + 7 * 24 * 60 * 60 * 1000 - 1)
                };
            case "mes":
                return {
                    start: new Date(today.getFullYear(), today.getMonth(), 1),
                    end: new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59)
                };
            case "trimestre":
                const quarter = Math.floor(today.getMonth() / 3);
                return {
                    start: new Date(today.getFullYear(), quarter * 3, 1),
                    end: new Date(today.getFullYear(), quarter * 3 + 3, 0, 23, 59, 59)
                };
            case "año":
                return {
                    start: new Date(today.getFullYear(), 0, 1),
                    end: new Date(today.getFullYear(), 11, 31, 23, 59, 59)
                };
            default:
                return null;
        }
    };

    const filteredIncomes = incomes.filter(income => {
        const matchesSearch = income.descripcion.toLowerCase().includes(searchTerm.toLowerCase()) ||
            income.fuente?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            income.cliente?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            income.proyecto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            income.notas?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesCategory = filterCategory === "all" || income.categoria === filterCategory;
        const matchesType = filterType === "all" || income.tipoIngreso === filterType;

        let matchesDate = true;
        if (filterDate !== "all") {
            const dateRange = getDateRange(filterDate);
            if (dateRange) {
                const incomeDate = new Date(income.fecha);
                matchesDate = incomeDate >= dateRange.start && incomeDate <= dateRange.end;
            }
        }

        return matchesSearch && matchesCategory && matchesType && matchesDate;
    });

    const sortedIncomes = [...filteredIncomes].sort((a, b) => {
        switch (sortBy) {
            case "descripcion":
                return a.descripcion.localeCompare(b.descripcion);
            case "cantidad":
                return b.cantidad - a.cantidad;
            case "fecha":
                return new Date(b.fecha) - new Date(a.fecha);
            case "categoria":
                return a.categoria.localeCompare(b.categoria);
            case "fuente":
                return (a.fuente || "").localeCompare(b.fuente || "");
            default:
                return 0;
        }
    });

    const getTotalAmount = () => {
        return filteredIncomes.reduce((total, income) => total + income.cantidad, 0);
    };

    const getCategoryStats = () => {
        const stats = {};
        filteredIncomes.forEach(income => {
            if (!stats[income.categoria]) {
                stats[income.categoria] = { total: 0, count: 0 };
            }
            stats[income.categoria].total += income.cantidad;
            stats[income.categoria].count += 1;
        });
        return Object.entries(stats)
            .sort(([, a], [, b]) => b.total - a.total)
            .slice(0, 5);
    };

    const getTypeStats = () => {
        const stats = {};
        filteredIncomes.forEach(income => {
            const type = incomeTypeLabels[income.tipoIngreso] || income.tipoIngreso;
            if (!stats[type]) {
                stats[type] = { total: 0, count: 0 };
            }
            stats[type].total += income.cantidad;
            stats[type].count += 1;
        });
        return Object.entries(stats);
    };

    const getIconComponent = (iconName) => {
        return iconOptions[iconName] || Tag;
    };

    const getCategoryData = (categoryName) => {
        return categories.find(cat => cat.nombre === categoryName);
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <Building2 className="w-16 h-16 text-blue-900 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">Acceso no autorizado</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-6">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate("/dashboard")}
                                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                <ChevronLeft className="w-6 h-6 text-slate-600" />
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-slate-900">Mis Ingresos</h1>
                                <p className="text-slate-600 font-medium">Gestione y analice todos sus ingresos</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => navigate("/income-categories")}
                                className="p-3 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                title="Gestionar categorías"
                            >
                                <Settings className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => navigate("/incomes/new")}
                                className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center gap-2 shadow-lg"
                            >
                                <Plus className="w-5 h-5" />
                                Registrar Ingreso
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-slate-600 font-medium">Total Ingresado</p>
                                <p className="text-2xl font-bold text-slate-900">
                                    ${getTotalAmount().toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                <TrendingUp className="w-6 h-6 text-green-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-slate-600 font-medium">Número de Ingresos</p>
                                <p className="text-2xl font-bold text-slate-900">{filteredIncomes.length}</p>
                            </div>
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Receipt className="w-6 h-6 text-blue-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-slate-600 font-medium">Promedio por Ingreso</p>
                                <p className="text-2xl font-bold text-slate-900">
                                    ${filteredIncomes.length > 0
                                        ? (getTotalAmount() / filteredIncomes.length).toLocaleString("es-MX", { minimumFractionDigits: 2 })
                                        : "0.00"
                                    }
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                                <BarChart3 className="w-6 h-6 text-purple-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-slate-600 font-medium">Categorías Usadas</p>
                                <p className="text-2xl font-bold text-slate-900">
                                    {new Set(filteredIncomes.map(i => i.categoria)).size}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                                <PieChart className="w-6 h-6 text-orange-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters and Search */}
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                        <div className="relative md:col-span-2">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Buscar ingresos..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                            />
                        </div>

                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                        >
                            <option value="all">Todas las categorías</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.nombre}>{category.nombre}</option>
                            ))}
                        </select>

                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                        >
                            <option value="all">Todos los tipos</option>
                            <option value="unico">Pago Único</option>
                            <option value="recurrente">Recurrente</option>
                            <option value="por_horas">Por Horas</option>
                        </select>

                        <select
                            value={filterDate}
                            onChange={(e) => setFilterDate(e.target.value)}
                            className="px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                        >
                            {periodOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>

                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                        >
                            <option value="fecha">Fecha</option>
                            <option value="descripcion">Descripción</option>
                            <option value="cantidad">Monto</option>
                            <option value="categoria">Categoría</option>
                            <option value="fuente">Fuente</option>
                        </select>
                    </div>
                </div>

                {/* Top Categories */}
                {getCategoryStats().length > 0 && (
                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm mb-8">
                        <h3 className="text-lg font-bold text-slate-900 mb-4">Top Categorías</h3>
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                            {getCategoryStats().map(([category, stats]) => {
                                const categoryData = getCategoryData(category);
                                const IconComponent = getIconComponent(categoryData?.icono);

                                return (
                                    <div key={category} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                                        <div
                                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                                            style={{
                                                backgroundColor: `${categoryData?.color || "#10B981"}15`,
                                                border: `2px solid ${categoryData?.color || "#10B981"}25`
                                            }}
                                        >
                                            <IconComponent
                                                className="w-5 h-5"
                                                style={{ color: categoryData?.color || "#10B981" }}
                                            />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-slate-900 text-sm">{category}</p>
                                            <p className="text-xs text-slate-600">
                                                ${stats.total.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Incomes List */}
                {loadingIncomes ? (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
                        <Loader2 className="w-8 h-8 animate-spin text-green-600 mx-auto mb-4" />
                        <p className="text-slate-600 font-medium">Cargando ingresos...</p>
                    </div>
                ) : sortedIncomes.length === 0 ? (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
                        <TrendingUp className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">
                            {incomes.length === 0 ? "No hay ingresos registrados" : "No se encontraron resultados"}
                        </h3>
                        <p className="text-slate-600 mb-6">
                            {incomes.length === 0
                                ? "Comience registrando su primer ingreso"
                                : "Intente ajustar los filtros de búsqueda"
                            }
                        </p>
                        {incomes.length === 0 && (
                            <button
                                onClick={() => navigate("/incomes/new")}
                                className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center gap-2 mx-auto"
                            >
                                <Plus className="w-5 h-5" />
                                Registrar Ingreso
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {sortedIncomes.map((income) => {
                            const categoryData = getCategoryData(income.categoria);
                            const IconComponent = getIconComponent(categoryData?.icono);

                            return (
                                <div
                                    key={income.id}
                                    className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all p-6"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4 flex-1">
                                            <div
                                                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                                                style={{
                                                    backgroundColor: `${categoryData?.color || "#10B981"}15`,
                                                    border: `2px solid ${categoryData?.color || "#10B981"}25`
                                                }}
                                            >
                                                <IconComponent
                                                    className="w-6 h-6"
                                                    style={{ color: categoryData?.color || "#10B981" }}
                                                />
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-lg font-semibold text-slate-900 mb-1">
                                                            {income.descripcion}
                                                        </h3>
                                                        <div className="flex items-center gap-4 text-sm text-slate-600 mb-2">
                                                            <span className="inline-flex items-center gap-1">
                                                                <Tag className="w-4 h-4" />
                                                                {income.categoria}
                                                            </span>
                                                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                                                {incomeTypeLabels[income.tipoIngreso] || income.tipoIngreso}
                                                            </span>
                                                            <span className="inline-flex items-center gap-1">
                                                                <Calendar className="w-4 h-4" />
                                                                {new Date(income.fecha).toLocaleDateString("es-MX")}
                                                            </span>
                                                        </div>

                                                        {/* Información adicional */}
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-600 mb-2">
                                                            {income.fuente && (
                                                                <span className="flex items-center gap-1">
                                                                    <Briefcase className="w-4 h-4" />
                                                                    {income.fuente}
                                                                </span>
                                                            )}
                                                            {income.cliente && (
                                                                <span className="flex items-center gap-1">
                                                                    <Users className="w-4 h-4" />
                                                                    {income.cliente}
                                                                </span>
                                                            )}
                                                            {income.proyecto && (
                                                                <span className="flex items-center gap-1">
                                                                    <FileText className="w-4 h-4" />
                                                                    {income.proyecto}
                                                                </span>
                                                            )}
                                                            {income.ubicacion && (
                                                                <span className="flex items-center gap-1">
                                                                    <MapPin className="w-4 h-4" />
                                                                    {income.ubicacion}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Para ingresos por horas */}
                                                        {income.tipoIngreso === "por_horas" && income.horasTrabajadas && income.tarifaPorHora && (
                                                            <div className="flex items-center gap-4 text-sm text-slate-600 mb-2">
                                                                <span className="flex items-center gap-1">
                                                                    <Clock className="w-4 h-4" />
                                                                    {income.horasTrabajadas} horas
                                                                </span>
                                                                <span className="flex items-center gap-1">
                                                                    <DollarSign className="w-4 h-4" />
                                                                    ${income.tarifaPorHora.toLocaleString("es-MX", { minimumFractionDigits: 2 })}/hora
                                                                </span>
                                                            </div>
                                                        )}

                                                        {income.metodoCobro && (
                                                            <p className="text-sm text-slate-600 mb-2">
                                                                <strong>Método:</strong> {income.metodoCobro}
                                                            </p>
                                                        )}

                                                        {income.notas && (
                                                            <p className="text-sm text-slate-600 mb-2">
                                                                {income.notas}
                                                            </p>
                                                        )}

                                                        {income.etiquetas && income.etiquetas.length > 0 && (
                                                            <div className="flex flex-wrap gap-1 mb-2">
                                                                {income.etiquetas.map((tag, index) => (
                                                                    <span
                                                                        key={index}
                                                                        className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium"
                                                                    >
                                                                        {tag}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {income.esRecurrente && (
                                                            <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                                                <Calendar className="w-3 h-3" />
                                                                Recurrente ({income.frecuenciaRecurrencia})
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="text-right ml-4">
                                                        <p className="text-2xl font-bold text-green-600">
                                                            ${income.cantidad.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                                                        </p>
                                                        <div className="flex items-center gap-2 mt-2">
                                                            <button
                                                                onClick={() => navigate(`/incomes/edit/${income.id}`)}
                                                                className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                                                                title="Editar ingreso"
                                                            >
                                                                <Edit3 className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(income.id, income.descripcion)}
                                                                disabled={loading}
                                                                className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                                                                title="Eliminar ingreso"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                            {income.comprobanteUrl && (
                                                                <a
                                                                    href={income.comprobanteUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                                                                    title="Ver comprobante"
                                                                >
                                                                    <FileText className="w-4 h-4" />
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Summary Card */}
                {incomes.length > 0 && (
                    <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-8 mt-8 text-white">
                        <h3 className="text-2xl font-bold mb-6">Resumen de Ingresos</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="text-center">
                                <p className="text-green-200 mb-2">Total del Período</p>
                                <p className="text-3xl font-bold">
                                    ${getTotalAmount().toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                                </p>
                            </div>
                            <div className="text-center">
                                <p className="text-green-200 mb-2">Ingresos Registrados</p>
                                <p className="text-3xl font-bold">{filteredIncomes.length}</p>
                            </div>
                            <div className="text-center">
                                <p className="text-green-200 mb-2">Promedio por Ingreso</p>
                                <p className="text-3xl font-bold">
                                    ${filteredIncomes.length > 0
                                        ? (getTotalAmount() / filteredIncomes.length).toLocaleString("es-MX", { minimumFractionDigits: 2 })
                                        : "0.00"
                                    }
                                </p>
                            </div>
                        </div>

                        {/* Top Categories in Summary */}
                        {getCategoryStats().length > 0 && (
                            <div className="mt-8 pt-6 border-t border-green-500">
                                <h4 className="text-lg font-semibold mb-4">Categorías con más ingresos</h4>
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                    {getCategoryStats().slice(0, 5).map(([category, stats]) => {
                                        const percentage = (stats.total / getTotalAmount()) * 100;
                                        return (
                                            <div key={category} className="text-center">
                                                <p className="text-green-200 text-sm mb-1">{category}</p>
                                                <p className="text-lg font-bold">
                                                    ${stats.total.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                                                </p>
                                                <p className="text-green-300 text-sm">
                                                    {percentage.toFixed(1)}%
                                                </p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Toast Message */}
            {message.text && (
                <div
                    className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg transition-all duration-300 z-50 max-w-md ${message.type === "error"
                            ? "bg-red-50 border border-red-200 text-red-800"
                            : "bg-green-50 border border-green-200 text-green-800"
                        }`}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {message.type === "error" ? (
                                <AlertCircle className="w-5 h-5 text-red-600" />
                            ) : (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            )}
                            <span className="font-semibold">{message.text}</span>
                        </div>
                        <button
                            onClick={() => setMessage({ text: "", type: "" })}
                            className="ml-4 p-1 hover:bg-white/50 rounded-full transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default XFinanceIncomesList;